import { useState, useEffect, useCallback, FormEvent, useMemo } from 'react';
import { api, ApiError } from '../api/client';
import './InventoryPanels.css';

type InventoryKind = 'serken' | 'regular';

interface InventoryPanel {
  id: string;
  kind: InventoryKind;
  reseller_id: string;
  email: string;
  capacity: number;
  source_panel_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface Toast {
  type: 'success' | 'error';
  message: string;
}

const PlusIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const UploadIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const XIcon = (): JSX.Element => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

export function InventoryPanels(): JSX.Element {
  const [kind, setKind] = useState<InventoryKind>('serken');
  const [rows, setRows] = useState<InventoryPanel[] | null>(null);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [showBulkCreds, setShowBulkCreds] = useState(false);
  const [editing, setEditing] = useState<InventoryPanel | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<InventoryPanel | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);

  const showToast = useCallback((type: Toast['type'], message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 4500);
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const params = new URLSearchParams({ kind });
      if (search.trim()) params.set('q', search.trim());
      const data = await api<InventoryPanel[]>(`/api/inventory-panels?${params.toString()}`);
      setRows(data);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to load inventory panels');
    }
  }, [showToast, kind, search]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api(`/api/inventory-panels/${deleteTarget.id}`, { method: 'DELETE' });
      showToast('success', `Removed ${deleteTarget.reseller_id}`);
      setDeleteTarget(null);
      await refresh();
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const totalCapacity = useMemo(
    () => (rows ?? []).reduce((acc, r) => acc + r.capacity, 0),
    [rows],
  );

  return (
    <div className="invp-root">
      <div className="invp-scroll">
        <header className="invp-header">
          <h1 className="invp-title">Inventory Panels</h1>
          <p className="invp-subtitle">
            Manage the panel rosters used by /teamlead Inventory Pull. <strong>Serken</strong>{' '}
            auto-populates from the order pipeline (1P + IT pushed). <strong>Regular</strong>{' '}
            is admin-managed for legacy panels.
          </p>

          <div className="invp-toolbar">
            <div className="invp-kind-tabs">
              <button
                type="button"
                className={`invp-kind-tab ${kind === 'serken' ? 'invp-kind-tab--active' : ''}`}
                onClick={() => {
                  if (kind !== 'serken') {
                    setKind('serken');
                    setRows(null);
                  }
                }}
              >
                Serken
              </button>
              <button
                type="button"
                className={`invp-kind-tab ${kind === 'regular' ? 'invp-kind-tab--active' : ''}`}
                onClick={() => {
                  if (kind !== 'regular') {
                    setKind('regular');
                    setRows(null);
                  }
                }}
              >
                Regular
              </button>
            </div>
            <div className="invp-toolbar-spacer" />
            <button
              type="button"
              className="invp-btn invp-btn--secondary"
              onClick={() => setShowBulkCreds(true)}
              title="Paste email,password,totp_secret rows to set credentials on existing inventory panels (used by License Panel Transformation)"
            >
              <UploadIcon />
              <span>Bulk update creds</span>
            </button>
            <button
              type="button"
              className="invp-btn invp-btn--secondary"
              onClick={() => setShowBulk(true)}
              title="Paste rows to add many panels at once"
            >
              <UploadIcon />
              <span>Bulk add</span>
            </button>
            <button
              type="button"
              className="invp-btn invp-btn--primary"
              onClick={() => setShowAdd(true)}
            >
              <PlusIcon />
              <span>Add panel</span>
            </button>
          </div>

          <div className="invp-search-row">
            <input
              type="search"
              className="invp-search"
              placeholder="Search by ID or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <span className="invp-count">
              {rows === null ? '—' : `${rows.length} panel${rows.length === 1 ? '' : 's'}`}
              {rows !== null && rows.length > 0 && (
                <> · {totalCapacity} total capacity</>
              )}
            </span>
          </div>
        </header>

        <section className="invp-table-card">
          {rows === null && <div className="invp-empty">Loading…</div>}

          {rows !== null && rows.length === 0 && (
            <div className="invp-empty">
              <h3 className="invp-empty-title">No panels in {kind}</h3>
              <p className="invp-empty-text">
                {kind === 'serken'
                  ? 'New panels land here automatically once they\'re pushed to 1P and Internal Tools.'
                  : 'Add legacy panels manually here so /teamlead can include them in inventory pulls.'}
              </p>
            </div>
          )}

          {rows !== null && rows.length > 0 && (
            <table className="invp-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Email</th>
                  <th className="invp-col-num">Capacity</th>
                  <th>Source</th>
                  <th>Notes</th>
                  <th className="invp-col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="invp-cell-mono">{r.reseller_id}</td>
                    <td className="invp-cell-mono">{r.email}</td>
                    <td className="invp-col-num">{r.capacity}</td>
                    <td className="invp-cell-faint">
                      {r.source_panel_id ? <span title={r.source_panel_id}>🔗 panel</span> : 'manual'}
                    </td>
                    <td className="invp-cell-faint invp-cell-notes">{r.notes ?? '—'}</td>
                    <td className="invp-col-actions">
                      <div className="invp-row-actions">
                        <button
                          type="button"
                          className="invp-btn invp-btn--secondary invp-btn--sm"
                          onClick={() => setEditing(r)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="invp-btn invp-btn--danger invp-btn--sm"
                          onClick={() => setDeleteTarget(r)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      {showAdd && (
        <PanelForm
          kind={kind}
          panel={null}
          onClose={() => setShowAdd(false)}
          onSuccess={(action, label) => {
            setShowAdd(false);
            showToast('success', `${label} ${action}`);
            void refresh();
          }}
          showToastError={(msg) => showToast('error', msg)}
        />
      )}

      {editing && (
        <PanelForm
          kind={editing.kind}
          panel={editing}
          onClose={() => setEditing(null)}
          onSuccess={(action, label) => {
            setEditing(null);
            showToast('success', `${label} ${action}`);
            void refresh();
          }}
          showToastError={(msg) => showToast('error', msg)}
        />
      )}

      {showBulk && (
        <BulkAddModal
          kind={kind}
          onClose={() => setShowBulk(false)}
          onSuccess={(added, skipped, errors) => {
            setShowBulk(false);
            const parts: string[] = [`${added} added`];
            if (skipped > 0) parts.push(`${skipped} skipped`);
            if (errors > 0) parts.push(`${errors} invalid`);
            showToast('success', parts.join(' · '));
            void refresh();
          }}
          showToastError={(msg) => showToast('error', msg)}
        />
      )}

      {showBulkCreds && (
        <BulkUpdateCredsModal
          kind={kind}
          onClose={() => setShowBulkCreds(false)}
          onSuccess={(updated, notFound, invalid) => {
            setShowBulkCreds(false);
            const parts: string[] = [`${updated} updated`];
            if (notFound > 0) parts.push(`${notFound} not found`);
            if (invalid > 0) parts.push(`${invalid} invalid`);
            showToast('success', parts.join(' · '));
            void refresh();
          }}
          showToastError={(msg) => showToast('error', msg)}
        />
      )}

      {deleteTarget && (
        <div className="invp-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="invp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="invp-modal-header">
              <h3 className="invp-modal-title">Remove this panel from inventory?</h3>
              <button type="button" className="invp-icon-btn" onClick={() => setDeleteTarget(null)}>
                <XIcon />
              </button>
            </div>
            <div className="invp-modal-body">
              <p>
                <span className="invp-cell-mono">{deleteTarget.reseller_id}</span> ·{' '}
                <span className="invp-cell-mono">{deleteTarget.email}</span> will no longer appear
                in /teamlead Inventory Pull. {deleteTarget.source_panel_id ? (
                  <>The configuration panel itself stays untouched. Auto-push won't re-add this entry; re-add manually if needed.</>
                ) : (
                  <>This entry was added manually.</>
                )}
              </p>
            </div>
            <div className="invp-modal-footer">
              <button
                type="button"
                className="invp-btn invp-btn--ghost"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="invp-btn invp-btn--danger"
                onClick={() => void handleDelete()}
                disabled={deleting}
              >
                {deleting ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className={`invp-toast invp-toast--${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

/* ─── Add / Edit form ─────────────────────────────────────── */

interface FormState {
  reseller_id: string;
  email: string;
  capacity: string; // string so we can show empty until user types
  notes: string;
}

function PanelForm({
  kind,
  panel,
  onClose,
  onSuccess,
  showToastError,
}: {
  kind: InventoryKind;
  panel: InventoryPanel | null;
  onClose: () => void;
  onSuccess: (action: 'added' | 'updated', label: string) => void;
  showToastError: (msg: string) => void;
}): JSX.Element {
  const [form, setForm] = useState<FormState>({
    reseller_id: panel?.reseller_id ?? '',
    email: panel?.email ?? '',
    capacity: panel ? String(panel.capacity) : '',
    notes: panel?.notes ?? '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const capN = Number(form.capacity);
    if (!Number.isInteger(capN) || capN < 1) {
      showToastError('Capacity must be a positive integer.');
      return;
    }
    setSaving(true);
    try {
      if (panel) {
        await api(`/api/inventory-panels/${panel.id}`, {
          method: 'PATCH',
          body: JSON.stringify({
            reseller_id: form.reseller_id,
            email: form.email,
            capacity: capN,
            notes: form.notes || null,
          }),
        });
        onSuccess('updated', form.reseller_id);
      } else {
        await api('/api/inventory-panels', {
          method: 'POST',
          body: JSON.stringify({
            kind,
            reseller_id: form.reseller_id,
            email: form.email,
            capacity: capN,
            notes: form.notes || null,
          }),
        });
        onSuccess('added', form.reseller_id);
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Save failed';
      showToastError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="invp-modal-overlay" onClick={onClose}>
      <div className="invp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="invp-modal-header">
          <h3 className="invp-modal-title">
            {panel ? 'Edit inventory panel' : `Add panel to ${kind}`}
          </h3>
          <button type="button" className="invp-icon-btn" onClick={onClose}>
            <XIcon />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="invp-modal-body">
            <div className="invp-field">
              <label className="invp-label">Reseller ID</label>
              <input
                className="invp-input invp-input-mono"
                value={form.reseller_id}
                onChange={(e) => setForm({ ...form, reseller_id: e.target.value })}
                placeholder="S4500"
                required
                autoFocus={!panel}
              />
            </div>
            <div className="invp-field">
              <label className="invp-label">Admin email</label>
              <input
                className="invp-input invp-input-mono"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="admin@example.com"
                required
              />
            </div>
            <div className="invp-field">
              <label className="invp-label">Capacity</label>
              <input
                className="invp-input"
                type="number"
                min="1"
                value={form.capacity}
                onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                placeholder="18"
                required
              />
              <p className="invp-hint">Number of non-admin license seats the team can delegate.</p>
            </div>
            <div className="invp-field">
              <label className="invp-label">
                Notes <span className="invp-label-suffix">(optional)</span>
              </label>
              <input
                className="invp-input"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="legacy panel · admin-takes-license · etc."
              />
            </div>
          </div>
          <div className="invp-modal-footer">
            <button
              type="button"
              className="invp-btn invp-btn--ghost"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="invp-btn invp-btn--primary" disabled={saving}>
              {saving ? 'Saving…' : panel ? 'Save changes' : 'Add panel'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Bulk add modal — paste · preview · confirm ─────────────── */

type ParsedStatus =
  | 'OK'
  | 'EXISTS'
  | 'DUPLICATE_IN_BATCH'
  | 'INVALID_FORMAT'
  | 'INVALID_EMAIL'
  | 'INVALID_RESELLER_ID'
  | 'INVALID_CAPACITY';

interface ParsedRow {
  index: number; // 0-based line in the original paste, after stripping
  reseller_id: string;
  email: string;
  capacity: number | null;
  status: ParsedStatus;
  message?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseBulkPaste(raw: string, existingEmails: Set<string>, existingResellerIds: Set<string>): ParsedRow[] {
  const lines = raw.split(/\r?\n/);
  const out: ParsedRow[] = [];
  const seenEmails = new Set<string>();
  const seenResellerIds = new Set<string>();
  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i] ?? '';
    const line = rawLine.trim();
    if (line.length === 0) continue;
    if (line.startsWith('#')) continue;

    // Auto-detect & skip header row
    const lower = line.toLowerCase().replace(/\s+/g, '');
    if (lower === 'reseller_id,admin_email,capacity' ||
        lower === 'resellerid,adminemail,capacity' ||
        lower === 'id,email,capacity') {
      continue;
    }

    // Split on tab or comma — pick whichever is present, fall back to comma
    const cells = (line.includes('\t') ? line.split('\t') : line.split(','))
      .map((c) => c.trim());

    if (cells.length !== 3) {
      out.push({
        index: i,
        reseller_id: cells[0] ?? '',
        email: cells[1] ?? '',
        capacity: null,
        status: 'INVALID_FORMAT',
        message: `Expected 3 cells, got ${cells.length}.`,
      });
      continue;
    }

    const reseller_id = cells[0];
    const email = cells[1];
    const capRaw = cells[2];

    if (!reseller_id || reseller_id.length === 0 || reseller_id.length > 64) {
      out.push({ index: i, reseller_id, email, capacity: null, status: 'INVALID_RESELLER_ID' });
      continue;
    }
    if (!email || !EMAIL_RE.test(email) || email.length > 254) {
      out.push({ index: i, reseller_id, email, capacity: null, status: 'INVALID_EMAIL' });
      continue;
    }
    const capN = Number(capRaw);
    if (!Number.isInteger(capN) || capN < 1 || capN > 100_000) {
      out.push({ index: i, reseller_id, email, capacity: null, status: 'INVALID_CAPACITY' });
      continue;
    }

    if (seenEmails.has(email) || seenResellerIds.has(reseller_id)) {
      out.push({
        index: i,
        reseller_id,
        email,
        capacity: capN,
        status: 'DUPLICATE_IN_BATCH',
      });
      continue;
    }
    if (existingEmails.has(email) || existingResellerIds.has(reseller_id)) {
      out.push({ index: i, reseller_id, email, capacity: capN, status: 'EXISTS' });
      seenEmails.add(email);
      seenResellerIds.add(reseller_id);
      continue;
    }

    seenEmails.add(email);
    seenResellerIds.add(reseller_id);
    out.push({ index: i, reseller_id, email, capacity: capN, status: 'OK' });
  }
  return out;
}

function BulkAddModal({
  kind,
  onClose,
  onSuccess,
  showToastError,
}: {
  kind: InventoryKind;
  onClose: () => void;
  onSuccess: (added: number, skipped: number, errors: number) => void;
  showToastError: (msg: string) => void;
}): JSX.Element {
  const [stage, setStage] = useState<'paste' | 'preview'>('paste');
  const [text, setText] = useState('');
  const [parsed, setParsed] = useState<ParsedRow[]>([]);
  const [existingEmails, setExistingEmails] = useState<Set<string>>(new Set());
  const [existingResellerIds, setExistingResellerIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  // Pre-fetch the current set of emails + reseller_ids in this kind's bucket
  // so the preview can flag EXISTS rows accurately. Cheap — list-only call.
  useEffect(() => {
    void (async (): Promise<void> => {
      try {
        const data = await api<InventoryPanel[]>(`/api/inventory-panels?kind=${kind}`);
        setExistingEmails(new Set(data.map((d) => d.email)));
        setExistingResellerIds(new Set(data.map((d) => d.reseller_id)));
      } catch {
        // Non-fatal — preview will just be more permissive
      }
    })();
  }, [kind]);

  const handleParse = (): void => {
    const result = parseBulkPaste(text, existingEmails, existingResellerIds);
    if (result.length === 0) {
      showToastError('No rows to parse.');
      return;
    }
    setParsed(result);
    setStage('preview');
  };

  const okRows = parsed.filter((r) => r.status === 'OK');
  const skipped = parsed.filter((r) => r.status === 'EXISTS' || r.status === 'DUPLICATE_IN_BATCH').length;
  const invalid = parsed.length - okRows.length - skipped;
  const totalCapacity = okRows.reduce((acc, r) => acc + (r.capacity ?? 0), 0);

  const confirm = async (): Promise<void> => {
    if (okRows.length === 0) return;
    setSubmitting(true);
    try {
      const body = {
        kind,
        rows: okRows.map((r) => ({
          reseller_id: r.reseller_id,
          email: r.email,
          capacity: r.capacity as number,
        })),
      };
      const result = await api<{ added: number; skipped_existing: number; errors: { code: string; message: string }[] }>(
        '/api/inventory-panels/bulk-add',
        { method: 'POST', body: JSON.stringify(body) },
      );
      onSuccess(result.added, result.skipped_existing + skipped, invalid + result.errors.length);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Bulk add failed';
      showToastError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="invp-modal-overlay" onClick={onClose}>
      <div className="invp-modal invp-modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="invp-modal-header">
          <h3 className="invp-modal-title">Bulk add to {kind}</h3>
          <button type="button" className="invp-icon-btn" onClick={onClose}>
            <XIcon />
          </button>
        </div>

        {stage === 'paste' && (
          <>
            <div className="invp-modal-body">
              <p className="invp-bulk-hint">
                One row per line: <code>reseller_id,email,capacity</code>. Tabs work too (paste from
                Sheets). Blank lines and lines starting with <code>#</code> are skipped. A header
                row is auto-detected.
              </p>
              <textarea
                className="invp-bulk-textarea"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={`reseller_id,admin_email,capacity\nS4500,admin@example.com,18\nS4501,admin@another.com,18`}
                spellCheck={false}
                autoFocus
              />
            </div>
            <div className="invp-modal-footer">
              <button type="button" className="invp-btn invp-btn--ghost" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="invp-btn invp-btn--primary"
                onClick={handleParse}
                disabled={text.trim().length === 0}
              >
                Parse
              </button>
            </div>
          </>
        )}

        {stage === 'preview' && (
          <>
            <div className="invp-modal-body">
              <div className="invp-bulk-summary">
                <span className="invp-bulk-summary-num invp-bulk-summary-num--ok">
                  {okRows.length} will be added
                </span>
                {skipped > 0 && (
                  <span className="invp-bulk-summary-meta"> · {skipped} skipped</span>
                )}
                {invalid > 0 && (
                  <span className="invp-bulk-summary-meta invp-bulk-summary-meta--bad">
                    {' '}
                    · {invalid} invalid
                  </span>
                )}
                <span className="invp-bulk-summary-meta">
                  {' '}
                  · total capacity: {totalCapacity}
                </span>
              </div>

              <div className="invp-bulk-table-wrap">
                <table className="invp-bulk-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Reseller ID</th>
                      <th>Email</th>
                      <th className="invp-col-num">Capacity</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.map((p, i) => (
                      <tr key={i} className={`invp-bulk-row invp-bulk-row--${p.status}`}>
                        <td>
                          <span className={`invp-bulk-tag invp-bulk-tag--${p.status}`}>
                            {p.status === 'OK'
                              ? 'OK'
                              : p.status === 'EXISTS'
                                ? 'exists'
                                : p.status === 'DUPLICATE_IN_BATCH'
                                  ? 'duplicate'
                                  : 'invalid'}
                          </span>
                        </td>
                        <td className="invp-cell-mono">{p.reseller_id || '—'}</td>
                        <td className="invp-cell-mono">{p.email || '—'}</td>
                        <td className="invp-col-num">{p.capacity ?? '—'}</td>
                        <td className="invp-cell-faint">{descForStatus(p)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="invp-modal-footer">
              <button
                type="button"
                className="invp-btn invp-btn--ghost"
                onClick={() => setStage('paste')}
                disabled={submitting}
              >
                Back to edit
              </button>
              <button
                type="button"
                className="invp-btn invp-btn--primary"
                onClick={() => void confirm()}
                disabled={submitting || okRows.length === 0}
              >
                {submitting ? 'Adding…' : `Confirm & add (${okRows.length})`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function descForStatus(p: ParsedRow): string {
  switch (p.status) {
    case 'OK':
      return 'will be added';
    case 'EXISTS':
      return 'already in this bucket';
    case 'DUPLICATE_IN_BATCH':
      return 'duplicate of an earlier line in this paste';
    case 'INVALID_FORMAT':
      return p.message ?? 'wrong column count';
    case 'INVALID_EMAIL':
      return 'not a valid email';
    case 'INVALID_RESELLER_ID':
      return 'reseller_id missing or too long';
    case 'INVALID_CAPACITY':
      return 'capacity must be a positive integer';
  }
}

// ---------------------------------------------------------------------------
// Bulk Update Credentials modal — paste email,password,totp_secret rows; the
// server matches by (kind,email) on inventory_panels and stores AES-encrypted
// values. Used by the License Panel Transformation feature; the operator
// pastes creds for an inventory cohort before running a transformation.
// ---------------------------------------------------------------------------

interface BulkCredsParsedRow {
  index: number;
  email: string;
  password: string;
  totp: string;
  status: 'OK' | 'INVALID_EMAIL' | 'EMPTY_PASSWORD' | 'INVALID_TOTP';
}

const TOTP_RE = /^[A-Z2-7]+$/;
const EMAIL_RE_FE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseBulkCredsPaste(raw: string): BulkCredsParsedRow[] {
  const lines = raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0 && !l.startsWith('#'));
  const out: BulkCredsParsedRow[] = [];
  lines.forEach((line, i) => {
    // Allow tabs OR commas
    const parts = line.split(/[\t,]/).map((p) => p.trim());
    const email = (parts[0] ?? '').toLowerCase();
    const password = parts[1] ?? '';
    const totp = (parts[2] ?? '').toUpperCase();
    let status: BulkCredsParsedRow['status'] = 'OK';
    if (!EMAIL_RE_FE.test(email)) status = 'INVALID_EMAIL';
    else if (password.length < 1) status = 'EMPTY_PASSWORD';
    else if (!TOTP_RE.test(totp) || totp.length < 16) status = 'INVALID_TOTP';
    out.push({ index: i, email, password, totp, status });
  });
  return out;
}

function BulkUpdateCredsModal({
  kind,
  onClose,
  onSuccess,
  showToastError,
}: {
  kind: InventoryKind;
  onClose: () => void;
  onSuccess: (updated: number, notFound: number, invalid: number) => void;
  showToastError: (msg: string) => void;
}): JSX.Element {
  const [stage, setStage] = useState<'paste' | 'preview'>('paste');
  const [text, setText] = useState('');
  const [parsed, setParsed] = useState<BulkCredsParsedRow[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const handleParse = (): void => {
    const result = parseBulkCredsPaste(text);
    if (result.length === 0) {
      showToastError('No rows to parse.');
      return;
    }
    setParsed(result);
    setStage('preview');
  };

  const okRows = parsed.filter((r) => r.status === 'OK');
  const invalid = parsed.length - okRows.length;

  const confirm = async (): Promise<void> => {
    if (okRows.length === 0) return;
    setSubmitting(true);
    try {
      const body = {
        kind,
        rows: okRows.map((r) => ({
          email: r.email,
          password: r.password,
          totp_secret: r.totp,
        })),
      };
      const result = await api<{
        updated: string[];
        not_found: string[];
        invalid: { email: string; reason: string }[];
      }>(
        '/api/inventory-panels/bulk-update-creds',
        { method: 'POST', body: JSON.stringify(body) },
      );
      onSuccess(result.updated.length, result.not_found.length, invalid + result.invalid.length);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Bulk update creds failed';
      showToastError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="invp-modal-overlay" onClick={onClose}>
      <div className="invp-modal invp-modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="invp-modal-header">
          <h3 className="invp-modal-title">Bulk update credentials — {kind}</h3>
          <button type="button" className="invp-icon-btn" onClick={onClose}>
            <XIcon />
          </button>
        </div>

        {stage === 'paste' && (
          <>
            <div className="invp-modal-body">
              <p className="invp-bulk-hint">
                One row per line: <code>email,password,totp_secret</code>. Tabs work too. The TOTP
                secret must be base32 (uppercase A–Z and digits 2–7) and at least 16 characters.
                The server matches by <code>(kind, email)</code> against existing inventory_panels
                rows; emails not found are reported back. Encrypted at rest.
              </p>
              <textarea
                className="invp-bulk-textarea"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={`admin@example.com,Hunter2!,JBSWY3DPEHPK3PXP\nadmin@another.com,SecretX,K7L4M2N9P5R8S6T2`}
                spellCheck={false}
                autoFocus
              />
            </div>
            <div className="invp-modal-footer">
              <button type="button" className="invp-btn invp-btn--ghost" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="invp-btn invp-btn--primary"
                onClick={handleParse}
                disabled={text.trim().length === 0}
              >
                Parse
              </button>
            </div>
          </>
        )}

        {stage === 'preview' && (
          <>
            <div className="invp-modal-body">
              <div className="invp-bulk-summary">
                <span className="invp-bulk-summary-num invp-bulk-summary-num--ok">
                  {okRows.length} will be updated
                </span>
                {invalid > 0 && (
                  <span className="invp-bulk-summary-meta invp-bulk-summary-meta--bad">
                    {' '}
                    · {invalid} invalid (skipped)
                  </span>
                )}
              </div>
              <div className="invp-bulk-table-wrap">
                <table className="invp-bulk-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Email</th>
                      <th>Password</th>
                      <th>TOTP secret</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.map((p, i) => (
                      <tr key={i} className={`invp-bulk-row invp-bulk-row--${p.status === 'OK' ? 'OK' : 'INVALID'}`}>
                        <td>
                          <span
                            className={`invp-bulk-tag invp-bulk-tag--${p.status === 'OK' ? 'OK' : 'INVALID'}`}
                          >
                            {p.status === 'OK' ? 'OK' : 'invalid'}
                          </span>
                        </td>
                        <td className="invp-cell-mono">{p.email || '—'}</td>
                        <td className="invp-cell-mono">
                          {p.password ? '•'.repeat(Math.min(p.password.length, 8)) : '—'}
                        </td>
                        <td className="invp-cell-mono">
                          {p.totp ? `${p.totp.slice(0, 4)}…${p.totp.slice(-4)}` : '—'}
                        </td>
                        <td className="invp-cell-faint">{descForCredsStatus(p.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="invp-modal-footer">
              <button
                type="button"
                className="invp-btn invp-btn--ghost"
                onClick={() => setStage('paste')}
                disabled={submitting}
              >
                Back to edit
              </button>
              <button
                type="button"
                className="invp-btn invp-btn--primary"
                onClick={() => void confirm()}
                disabled={submitting || okRows.length === 0}
              >
                {submitting ? 'Updating…' : `Confirm & update (${okRows.length})`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function descForCredsStatus(s: BulkCredsParsedRow['status']): string {
  switch (s) {
    case 'OK':
      return 'will encrypt + update inventory_panels';
    case 'INVALID_EMAIL':
      return 'invalid email format';
    case 'EMPTY_PASSWORD':
      return 'password is empty';
    case 'INVALID_TOTP':
      return 'TOTP must be base32 (A-Z, 2-7), at least 16 chars';
  }
}
